/**
 *
 * ManageBag
 *
 */
import * as React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch, useSelector } from 'react-redux';
import { useArsenalSlice } from 'app/pages/Arsenal/slice';
import { useNavigate } from 'react-router-dom';
import { selectArsenal } from 'app/pages/Arsenal/slice/selectors';
import { APIBallData } from 'app/pages/Arsenal/slice/types';

interface Props {
  arsenal?: any;
}

interface SortableItemProps {
  id: string;
  ball: APIBallData;
  onRemove: (ballId: string) => void;
}

function SortableItem({ id, ball, onRemove }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      secondaryAction={
        <IconButton
          edge="end"
          aria-label="delete"
          onClick={() => onRemove(ball.id)}
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      }
      disablePadding
      sx={{
        backgroundColor: isDragging ? 'action.hover' : 'transparent',
        borderRadius: 1,
        mb: 0.5,
      }}
    >
      <div
        {...attributes}
        {...listeners}
        style={{ width: '100%', touchAction: 'none' }}
      >
        <ListItemButton sx={{ py: 1.5 }} disableRipple>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DragIndicatorIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={ball.ball_name}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItemButton>
      </div>
    </ListItem>
  );
}

export function ManageBag(props: Props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { actions } = useArsenalSlice();
  const { balls } = useSelector(selectArsenal);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedBalls, setSelectedBalls] = React.useState<APIBallData[]>([]);
  const [autocompleteValue, setAutocompleteValue] =
    React.useState<APIBallData | null>(null);

  // const sensors = useSensors(
  //   useSensor(PointerSensor, {
  //     activationConstraint: {
  //       delay: 150,
  //       distance: 2,
  //     },
  //   }),
  //   useSensor(TouchSensor, {
  //     activationConstraint: {
  //       delay: 850,
  //       tolerance: 5,
  //     },
  //   }),
  //   useSensor(KeyboardSensor, {
  //     coordinateGetter: sortableKeyboardCoordinates,
  //   }),
  // );

  const handleAddBall = (ball: APIBallData | null) => {
    if (ball && !selectedBalls.find(b => b.id === ball.id)) {
      setSelectedBalls([...selectedBalls, ball]);
    }
    setAutocompleteValue(null);
  };

  const handleRemoveBall = (ballId: string) => {
    setSelectedBalls(selectedBalls.filter(b => b.id !== ballId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedBalls.findIndex(b => b.id === active.id);
      const newIndex = selectedBalls.findIndex(b => b.id === over.id);
      setSelectedBalls(arrayMove(selectedBalls, oldIndex, newIndex));
    }
  };

  const availableBalls =
    balls?.filter(ball => !selectedBalls.find(b => b.id === ball.id)) || [];

  if (!balls) return <></>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '95vw' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Manage Your Bag
      </Typography>

      <Stack spacing={3}>
        {/* Autocomplete Section */}
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Add Balls to Your Bag
            </Typography>
            <Autocomplete
              options={availableBalls}
              getOptionLabel={option => option.ball_name}
              value={autocompleteValue}
              onChange={(_, value) => handleAddBall(value)}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Search and select a ball"
                  size={isMobile ? 'small' : 'medium'}
                />
              )}
              fullWidth
              clearOnBlur
              blurOnSelect
              noOptionsText="No available balls"
            />
          </CardContent>
        </Card>

        {/* Selected Balls List Section */}
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Your Selected Balls ({selectedBalls.length})
            </Typography>

            {selectedBalls.length > 0 ? (
              <DndContext
                // sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selectedBalls.map(b => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <List sx={{ width: '100%' }}>
                    {selectedBalls.map(ball => (
                      <SortableItem
                        key={ball.id}
                        id={ball.id}
                        ball={ball}
                        onRemove={handleRemoveBall}
                      />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            ) : (
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: 'action.hover',
                }}
              >
                <Typography color="textSecondary" variant="body2">
                  No balls selected yet. Add balls from the list above to get
                  started.
                </Typography>
              </Paper>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
